import { Navigate, Route } from "react-router";

import { queryClient } from "@/providers/QueryClient";
import { loadSearchTablePreferences } from "@/shared/domain/config/loaders/loadSearchTablePreferences";
import { QueryTarget } from "@/shared/domain/query";
import { makeViewsLoader } from "@/shared/domain/views/loaders/loadViews";
import { Goto } from "@/shared/navigation";
import { metricsWidgetRoutePatterns } from "@/shared/navigation/Goto";
import { composeLoaders } from "@/shared/routing/composeLoaders";
import { featureFlagRoute } from "@/shared/routing/featureFlagRoute";
import { InheritOutletContext } from "@/shared/routing/InheritOutletContext";
import { RedirectToDefaultTab } from "@/shared/routing/RedirectToDefaultTab";

import { Device } from "./Device";
import { DeviceConfigurations } from "./DeviceConfigurations";
import { DeviceConfigurationsRedirect } from "./DeviceConfigurationsRedirect";
import { DeviceDatasets } from "./DeviceDatasets";
import { DeviceDetails } from "./DeviceDetails";
import { DeviceFiles } from "./DeviceFiles";
import { DeviceMetrics } from "./DeviceMetrics";
import { Devices } from "./Devices";
import { DeviceSessions } from "./DeviceSessions";
import { DeviceTokens } from "./DeviceTokens";

export const DevicesRoutes = () => [
  /*
   * Opening the devices list must seed saved column order / page size
   * before <Devices /> mounts — `useResourceView` snapshots `preferences`
   * once via `React.useState` and never re-reads.
   */
  <Route
    key="devices-preferences-layout"
    loader={composeLoaders(
      loadSearchTablePreferences(
        queryClient,
        "device_search_table_preferences",
      ),
      // See `DatasetsRoutes`: resolves a `?view=` link before the list mounts.
      makeViewsLoader(queryClient, { target: QueryTarget.Devices }),
    )}
    element={<InheritOutletContext />}
  >
    <Route index element={<Devices />} />
  </Route>,
  <Route key={"id"} path=":deviceId" element={<Device />}>
    {/* Bare `/devices/:deviceId` has no tab to render; redirect to the default. */}
    <Route index element={<Navigate to="datasets" replace />} />
    <Route
      path="datasets"
      loader={composeLoaders(
        loadSearchTablePreferences(
          queryClient,
          "dataset_search_table_preferences",
        ),
        // See `DatasetsRoutes`: resolves a `?view=` link before the list mounts.
        makeViewsLoader(queryClient, { target: QueryTarget.Datasets }),
      )}
      element={<DeviceDatasets />}
    />
    {/* `*` is the path of the folder open in the device's own section. */}
    {featureFlagRoute({
      path: "configurations/*",
      flagName: "release-asset-store",
      redirectTo: ({ params }) =>
        `/devices/${encodeURIComponent(params.deviceId ?? "")}/datasets`,
      element: <DeviceConfigurations />,
    })}
    <Route path="files" element={<DeviceFiles />} />
    {/*
        Opening the Sessions tab must seed saved column order / page size
        before <DeviceSessions /> mounts — `useResourceView` snapshots
        `preferences` once via `React.useState` and never re-reads.
        `featureFlagRoute` already uses its loader slot for the flag
        gate, so this pathless wrapper Route runs the preferences loader.
    */}
    <Route
      key="sessions-preferences-layout"
      loader={loadSearchTablePreferences(
        queryClient,
        "session_search_table_preferences",
      )}
      element={<InheritOutletContext />}
    >
      {featureFlagRoute({
        path: "sessions",
        flagName: "release-sessions",
        redirectTo: ({ params }) =>
          `/devices/${encodeURIComponent(params.deviceId ?? "")}/datasets`,
        element: <DeviceSessions />,
      })}
    </Route>
    {/* The optional `:dashboardId?` segment carries the active saved
        dashboard (mirrors the top-level /metrics/:dashboardId? route);
        bare .../metrics is the "no dashboard selected" view. */}
    {featureFlagRoute({
      path: "metrics/:dashboardId?",
      flagName: "release-metrics",
      redirectTo: ({ params }) =>
        `/devices/${encodeURIComponent(params.deviceId ?? "")}/datasets`,
      element: <DeviceMetrics />,
    })}
    {/* The shared widget-editor patterns are absolute (MetricsDashboard
        matches against them), so this parent's prefix is stripped. */}
    {Object.values(metricsWidgetRoutePatterns({ deviceScoped: true })).map(
      (pattern) =>
        featureFlagRoute({
          path: pattern.replace("/devices/:deviceId/", ""),
          flagName: "release-metrics",
          redirectTo: ({ params }) => `/devices/${params.deviceId}/datasets`,
          element: <DeviceMetrics />,
        }),
    )}
    <Route path="details" element={<DeviceDetails />} />
    <Route path="tokens" element={<DeviceTokens />} />
    {/* Unknown subpath (typo, removed tab, stale bookmark): redirect to the default. */}
    <Route
      path="*"
      element={
        <RedirectToDefaultTab
          buildPath={({ deviceId }) =>
            `${Goto.route.Devices}/${encodeURIComponent(deviceId ?? "")}/datasets`
          }
        />
      }
    />
  </Route>,
];

/**
 * `/device-configurations/:universalDeviceId/*`, where links to a device's
 * files land (see `Goto.route.DeviceConfigurationsByUniversalId`). It is a
 * top-level route because under `/devices` the segment after the prefix is
 * always the device ID the customer chose, never the `dv_` ID.
 */
export const DeviceConfigurationsRedirectRoute = () =>
  featureFlagRoute({
    path: `${Goto.route.DeviceConfigurationsByUniversalId}/:universalDeviceId/*`,
    flagName: "release-asset-store",
    redirectTo: Goto.route.Devices,
    element: <DeviceConfigurationsRedirect />,
  });
